package com.evoapps.evoshift.domain.usecase.schedule

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.RosterPublishing
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.schedule.SchedulePlan
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase

/** What applying a plan did. */
data class AutoScheduleApplied(
    /** Shifts given to someone. */
    val assigned: Int,
    /** Shifts emptied by a replan (offered to the team again). */
    val cleared: Int,
    /** Shifts that changed since the preview (someone assigned them, times edited): left alone. */
    val skipped: List<String>,
    /** Shifts priced afterwards; null when pricing failed or is not allowed (best effort). */
    val priced: Int?
)

/**
 * Writes a [SchedulePlan] from [PlanAutoScheduleUseCase] in one batch: the assignments (the shift
 * stops being open, a marketplace offer on it is withdrawn) and, for a replan, the shifts it
 * could not fill again (emptied and offered to the team, so publishing does not drop them). A
 * shift that changed since the preview is skipped, never overwritten. Afterwards the roster's
 * shifts are priced like a manual assignment (best effort: only financial managers may).
 */
class ApplyAutoScheduleUseCase(
    private val shiftRepository: ShiftRepository,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    companion object {
        /** Stamped as metadata.updatedBy when no editor is given, like ApplyWeeklySkeletonUseCase.CREATED_BY. */
        const val UPDATED_BY = "SYSTEM_AUTO_SCHEDULE"

        /**
         * Tag on a shift the rules assigned (SH-53), so it can be told apart later (how often the
         * manager keeps the proposal). Removed again when the shift is emptied by a replan.
         */
        const val TAG = "AUTO_ASSIGNED"
    }

    /**
     * @param onlyShiftIds the shifts the manager accepted (null: the whole plan).
     * @param editorUserId the manager who accepted it, stamped as metadata.updatedBy (null: [UPDATED_BY]).
     */
    suspend operator fun invoke(
        plan: SchedulePlan,
        onlyShiftIds: Set<String>? = null,
        editorUserId: String? = null
    ): Result<AutoScheduleApplied> = runCatching {
        val current = shiftRepository.getShiftsForRoster(plan.rosterId).getOrThrow().associateBy { it.id }
        val now = AppClock.now().toString()
        val skipped = mutableListOf<String>()
        fun accepted(id: String) = onlyShiftIds == null || id in onlyShiftIds

        /** The shift as the preview saw it, or null (and skipped) when it changed since. */
        fun unchanged(shiftId: String, previousUserId: String?, plannedTime: TimeRange): Shift? {
            val shift = current[shiftId]
            if (shift == null || shift.userId != previousUserId || shift.plannedTime != plannedTime) {
                skipped += shiftId
                return null
            }
            return shift
        }

        fun Shift.reassigned(userId: String?, name: String?) = copy(
            userId = userId,
            publishedUserId = RosterPublishing.publishedAssignee(this, userId),
            userDisplayName = name,
            isOpen = userId == null,
            swapRequest = null,
            conflictMetadata = null,
            version = version + 1,
            hasUnpublishedChanges = isPublished || hasUnpublishedChanges,
            metadata = metadata.copy(updatedAt = now, updatedBy = editorUserId ?: UPDATED_BY),
            tags = if (userId != null) (tags + TAG).distinct() else tags - TAG
        )

        val assigned = plan.assignments.filter { accepted(it.shiftId) }.mapNotNull { planned ->
            unchanged(planned.shiftId, planned.previousUserId, planned.plannedTime)?.reassigned(planned.userId, planned.displayName)
        }
        val cleared = plan.unfilled.filter { it.previousUserId != null && accepted(it.shiftId) }.mapNotNull { unfilled ->
            unchanged(unfilled.shiftId, unfilled.previousUserId, unfilled.plannedTime)?.reassigned(null, null)
        }
        val writes = assigned + cleared
        if (writes.isNotEmpty()) shiftRepository.updateShifts(writes).getOrThrow()
        val priced = if (writes.isEmpty()) 0 else calculateShiftCost?.refreshRoster(plan.rosterId)?.getOrNull()
        AutoScheduleApplied(assigned.size, cleared.size, skipped, priced)
    }
}
